import { makeMPServerRequest } from "../assets/helpers";
import { getNoteStatusFromOrderStatus } from "./orders";
import { formatDate } from "../assets/helpers_legacy.js";

import { Session, SessionWithOrdersAndType, SessionsWithPagination } from "../types/session-interfaces";
import { Order } from "../types/order-interfaces";

/**
 * Updates an existing session with the given session ID and new data.
 * 
 * Sends a PUT request to update the session, and if successful, returns the updated session data.
 * 
 * @param {string} sessionId - The ID of the session to update.
 * @param {Partial<Session>} updateSessionData - The data to update the session with.
 * @returns {Promise<Session>} - A promise that resolves to the updated session.
 * @throws {Error} - Throws an error if the response status is not "success" or if there is an exception during the request.
 */
export async function updateSession(
  sessionId: string,
  updateSessionData: Partial<Session>,
): Promise<Session> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/sessions/${sessionId}`,
      "PUT",
      "json",
      JSON.stringify(updateSessionData),
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Session;
    } else {
      throw new Error(
        `Unexpected response while updating session "${sessionId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while updating session "${sessionId}": ${error}`
    );
  }
}

/**
 * Archives a session by session ID.
 * 
 * Sends a POST request to archive the session. If successful, returns the session data.
 * 
 * @param {string} sessionId - The ID of the session to archive.
 * @returns {Promise<Session>} - A promise that resolves to the archived session data.
 * @throws {Error} - Throws an error if the response status is not "success" or if there is an exception during the request.
 */
export async function archiveSession(
  sessionId: string,
): Promise<Session> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/sessions/${sessionId}/archive`,
      "PUT",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Session;
    } else {
      throw new Error(
        `Unexpected response while archiving session "${sessionId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while archiving session "${sessionId}": ${error}`
    );
  }
}

/**
 * Unarchives a previously archived session by session ID.
 * 
 * Sends a POST request to restore (unarchive) the session. If successful, returns the session data.
 * 
 * @param {string} sessionId - The ID of the session to restore.
 * @returns {Promise<Session>} - A promise that resolves to the restored session data.
 * @throws {Error} - Throws an error if the response status is not "success" or if there is an exception during the request.
 */
export async function unarchiveSession(
  sessionId: string,
): Promise<Session> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/sessions/${sessionId}/unarchive`,
      "PUT",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Session;
    } else {
      throw new Error(
        `Unexpected response while archiving session "${sessionId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while archiving session "${sessionId}": ${error}`
    );
  }
}

/**
 * Attaches orders to corresponding sessions and returns a new array of sessions 
 * with added information about orders, note status, and type.
 * 
 * This function creates a mapping of orders to session IDs, then for each session, 
 * it associates the relevant orders, calculates the note status based on the orders, 
 * and includes a 'type' property with the value "session".
 * 
 * @param {Session[]} sessions - An array of sessions to which orders will be attached.
 * @param {Order[]} orders - An array of orders that will be grouped by sessionId and attached to the sessions.
 * @returns {SessionWithOrdersAndType[]} - A new array of sessions, each enhanced with:
 *   - `orders`: The list of orders associated with the session.
 *   - `noteStatus`: The status of the order attached to the session formated for datatable (calculated via `getNoteStatusFromOrderStatus`).
 *   - `orderStatus`: The status of the orders attached to the session (calculated via `getNoteStatusFromOrderStatus`).
 *   - `type`: The value `"session"`, indicating the type of the session.
 */
export function attachOrdersToSessions(
  sessions: Session[],
  orders: Order[]
): SessionWithOrdersAndType[] {
  const orderMap = new Map<string, Order[]>();


  for (const order of orders) {
    if (!order.sessionId) {
      continue
    }
    if (!orderMap.has(order.sessionId)) {
      orderMap.set(order.sessionId, []);
    }
    orderMap.get(order.sessionId)!.push(order);
  }


  return sessions.map(session => {
    const { noteStatus, orderStatus, matchingOrder } = getNoteStatusFromOrderStatus(
      orderMap.get(session.id) || []
    )

    return {
      ...session,
      orders: orderMap.get(session.id) || [],
      noteStatus,
      order: matchingOrder,
      orderStatus,
      lastModified: formatDate(session.updatedAt, session.updatedAt),
      type: 'session',
    }
  });
}

export async function getSessionList(filterQuery?: string): Promise<Session[]> {
  try {
    const endpoint = filterQuery
      ? `self/sessions/list?${filterQuery}`
      : "self/sessions/list";

    const mpResponse = await makeMPServerRequest(
      endpoint,
      "GET",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Session[];
    } else {
      throw new Error(
        `Unexpected response while getting session list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting session list: ${error}`
    );
  }
}

export async function getSessionListPagination(paginationParams: { perPage: number, currentPage: number, orderBy: string, order: string }): Promise<SessionsWithPagination> {
  const { perPage, currentPage, orderBy, order } = paginationParams;
  // the "My Sessions" ItemList on the homepage is designed to show only active sessions, so we filter out archived sessions by default
  // using the &includeArchived=exclude query parameter
  const params = new URLSearchParams({
    perPage: perPage.toString(),
    currentPage: currentPage.toString(),
    orderBy,
    order,
    includeArchived: 'exclude',
    includeSessionsInFolders: 'exclude'
  });
  const paginationQuery = '?' + params.toString();

  try {
    const mpResponse = await makeMPServerRequest(
      "self/sessions/list" + paginationQuery,
      "GET",
      "json",
    );

    if (mpResponse.status === "success" && mpResponse.meta) {
      return {
        sessions: mpResponse.data,
        pagination: mpResponse.meta.pagination,
      } as SessionsWithPagination;
    } else {
      throw new Error(
        `Unexpected response while getting session list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting session list: ${error}`
    );
  }
}